import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';

interface MapViewProps {
  vehicles: Array<Record<string, unknown>>;
  npcCounts: Record<string, unknown>;
}

export function MapView({ vehicles, npcCounts }: MapViewProps) {
  const { data: facilities } = useQuery({
    queryKey: ['facilities'],
    queryFn: () => api.getFacilities(),
  });

  const { data: projects } = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.getFacilities(), // Reuse endpoint for now
  });

  const facilityList = (facilities as { facilities?: Array<Record<string, unknown>> })?.facilities || [];
  const projectList = (projects as { facilities?: Array<Record<string, unknown>> })?.facilities || [];
  const facilityNPCs = (npcCounts as { facilities?: Record<number, number> })?.facilities || {};

  return (
    <div className="space-y-6">
      {/* Colony Map */}
      <div className="card">
        <h2 className="text-lg font-bold mb-4">🗺️ Colony Map</h2>
        <div className="relative bg-lunar-900 rounded-lg p-4 min-h-[400px]">
          {/* Simple grid-based map */}
          <div className="grid grid-cols-10 gap-2">
            {facilityList.map((facility) => (
              <div
                key={facility.id as number}
                className="col-span-2 bg-lunar-700 rounded-lg p-3 text-center"
                style={{
                  gridColumn: `${Math.floor((facility.x as number || 0) / 10) + 1}`,
                  gridRow: `${Math.floor((facility.y as number || 0) / 10) + 1}`,
                }}
              >
                <div className="text-2xl mb-1">{facility.icon as string}</div>
                <div className="text-xs font-medium">{facility.name as string}</div>
                <div className="text-xs text-lunar-400">
                  👥 {facilityNPCs[facility.id as number] || 0}
                </div>
                {(facility.impact_points as number) > 0 && (
                  <div className="text-xs text-mars-400">
                    ⚡ {facility.impact_points as number}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Vehicles overlay */}
          {vehicles.map(vehicle => (
            <div
              key={vehicle.id as number}
              className="absolute bg-mars-500 text-white text-xs px-2 py-1 rounded-full"
              style={{
                left: `${((vehicle.current_location_id as number) % 10) * 10}%`,
                top: `${Math.floor((vehicle.current_location_id as number) / 10) * 10}%`,
              }}
            >
              🚗 {vehicle.name as string}
              {(vehicle.passenger_count as number) > 0 && ` (${vehicle.passenger_count as number})`}
            </div>
          ))}
        </div>
      </div>

      {/* Location List */}
      <div className="grid grid-cols-2 gap-4">
        <div className="card">
          <h3 className="font-bold mb-3">🏭 Facilities</h3>
          <div className="space-y-2">
            {facilityList.map((f) => (
              <div key={f.id as number} className="flex items-center justify-between bg-lunar-700 rounded-lg p-2">
                <div className="flex items-center gap-2">
                  <span>{f.icon as string}</span>
                  <span className="text-sm">{f.name as string}</span>
                </div>
                <span className="text-xs text-lunar-300">
                  👥 {facilityNPCs[f.id as number] || 0}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="card">
          <h3 className="font-bold mb-3">🚧 Projects</h3>
          <div className="space-y-2">
            {projectList.filter((f) => (f.id as number) >= 100).map((p) => (
              <div key={p.id as number} className="flex items-center justify-between bg-lunar-700 rounded-lg p-2">
                <div className="flex items-center gap-2">
                  <span>🏗️</span>
                  <span className="text-sm">{p.name as string}</span>
                </div>
                <div className="text-xs text-lunar-300">
                  <div>👥 {(npcCounts as { projects?: Record<number, number> })?.projects?.[p.id as number] || 0}</div>
                  <div className="text-mars-400">{p.progress as number}%</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
